import React, { useCallback, useState, useMemo, useRef, useEffect } from 'react';
import LinearGradient from 'utils/packages/linearGradient';
import { View, StyleSheet, ScrollView } from 'react-native';
import { rem } from 'design-system/values';
import TimePeriod from 'design-system/LineChart/TimePeriod';
import AnimatedMarker from 'design-system/LineChart/AnimatedMarker';
import useColors from 'hooks/useColors';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useAppSelector } from 'store/hooks';
import { noop } from 'lodash';
import { mapPortfolioToStockChartPeriod, mapStockToPortfolioChartPeriod } from '../utils';
import { selectTradingAccount } from '../reducers/selectors';
import { isWeb } from '../../../constants';
const PERIODS = ['1D', '1W', '1M', '3M', 'YTD', '1Y', 'MAX'];
const PERIODS_WITHOUT_1D = ['1W', '1M', '3M', 'YTD', '1Y', 'MAX'];
const paddingHorizontal = rem(16);
const gradientEnd = { x: 1, y: 1 };
const gradientStart = { x: 0, y: 1 };
const viewPadding = rem(16 * 2 + 6 * 2);
const timePeriodWidth = rem(10 * 2 + 36 + 4);
const PeriodSwitch = ({ onSelect, defaultPeriod, isInTab, backgroundColor, scrollToEnd, }) => {
    const { width } = useAppFrameDimensions(isInTab || isWeb);
    const scrollViewRef = useRef(null);
    const tradingAccount = useAppSelector(selectTradingAccount);
    const has1DChart = tradingAccount?.isAlpaca;
    const colors = useColors();
    const periods = React.useMemo(() => {
        if (has1DChart) {
            return PERIODS;
        }
        return PERIODS_WITHOUT_1D;
    }, [has1DChart]);
    const gradientColors = useMemo(() => ({
        leftGradient: [
            colors.cards.onDark,
            //@ts-ignore
            `${colors.cards.onDark}00`,
        ],
        rightGradient: [
            //@ts-ignore
            `${colors.cards.onDark}00`,
            colors.cards.onDark,
        ],
    }), [colors.cards.onDark]);
    const requiredWidth = periods.length * timePeriodWidth;
    const [selected, setSelected] = useState(defaultPeriod);
    useEffect(() => {
        if (scrollToEnd) {
            const timeout = setTimeout(() => {
                scrollViewRef.current?.scrollToEnd();
            }, 100);
            return () => {
                clearTimeout(timeout);
            };
        }
        return noop;
    }, [scrollToEnd]);
    const selectPeriod = useCallback((period) => {
        const parsedPeriod = mapPortfolioToStockChartPeriod(period);
        onSelect(parsedPeriod);
        setSelected(parsedPeriod);
    }, [onSelect]);
    if (requiredWidth + viewPadding <= width) {
        return (<View style={styles.container}>
        <AnimatedMarker isInTab={isInTab} periods={periods} selectedPeriod={selected} backgroundColor={backgroundColor} paddingHorizontal={paddingHorizontal}/>
        {periods.map((period) => (<View style={styles.flex} key={period}>
            <TimePeriod darkActiveIndicator selectedPeriod={selected} isSelected={period === selected} setSelectedPeriod={selectPeriod} period={mapStockToPortfolioChartPeriod(period)} activeTextColor={colors.text.textOnBrand}/>
          </View>))}
      </View>);
    }
    return (<View>
      <ScrollView horizontal ref={scrollViewRef} showsHorizontalScrollIndicator={false} contentContainerStyle={styles.contentContainer}>
        <View style={[styles.container, { width: requiredWidth }]}>
          <AnimatedMarker isInTab periods={periods} selectedPeriod={selected} viewWidth={timePeriodWidth} backgroundColor={backgroundColor}/>
          {periods.map((period) => (<View style={styles.flex} key={period}>
              <TimePeriod periods={periods} darkActiveIndicator selectedPeriod={selected} viewWidth={timePeriodWidth} scrollViewRef={scrollViewRef} isSelected={period === selected} setSelectedPeriod={selectPeriod} period={mapStockToPortfolioChartPeriod(period)} activeTextColor={colors.text.textOnBrand}/>
            </View>))}
        </View>
      </ScrollView>
      <View style={styles.leftGradient}>
        <LinearGradient end={gradientEnd} start={gradientStart} style={styles.gradientView} colors={gradientColors.leftGradient}/>
      </View>
      <View style={styles.rightGradient}>
        <LinearGradient end={gradientEnd} start={gradientStart} style={styles.gradientView} colors={gradientColors.rightGradient}/>
      </View>
    </View>);
};
const styles = StyleSheet.create({
    container: {
        flex: 1,
        flexDirection: 'row',
    },
    gradientView: {
        flex: 1,
    },
    flex: {
        flex: 1,
        alignItems: 'center',
    },
    leftGradient: {
        left: 0,
        top: rem(16),
        width: rem(16),
        bottom: rem(16),
        position: 'absolute',
    },
    rightGradient: {
        right: 0,
        top: rem(16),
        width: rem(16),
        bottom: rem(16),
        position: 'absolute',
    },
    contentContainer: {
        paddingHorizontal: rem(6),
    },
});
export default PeriodSwitch;
